import { Autocomplete, Box, Grid, Typography } from "@mui/material";
import { format, isAfter } from "date-fns";
import { useTranslation } from "react-i18next";

import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { isDateBefore } from "../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import { ConfirmationDialog } from "../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../common/helpers/isEmpty";
import { Nullable } from "../../../../common/types/nullable";
import { Employment } from "../../../employment/employmentApi";
import { endEmployment } from "../../../employment/employmentSlice";
import { EmploymentBasicInfoFormValues, EmploymentEndReason } from "./types";

interface Props {
  employment: Employment;
  canEdit: boolean;
  isAdmin: boolean;
}

export const BasicInfo = ({ employment, canEdit, isAdmin }: Props) => {
  const [edit, setEdit] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const pendingSubmitDataRef = useRef<EmploymentBasicInfoFormValues | null>(
    null,
  );

  const TESNames = useAppSelector((state) => state.localized.tes);
  const tempEmploymentReasons = useAppSelector(
    (state) => state.localized.tempEmploymentReason,
  );
  const workTitles = useAppSelector((state) => state.localized.workTitle);
  const employmentTypes = useAppSelector(
    (state) => state.localized.employmentType,
  );
  const employmentEndReasons = useAppSelector(
    (state) => state.localized.employmentEndReason,
  );
  // translated end reason options
  const endReasonOptions = Object.keys(EmploymentEndReason).map((reason) => ({
    id: reason,
    name: employmentEndReasons?.[reason] || reason,
  }));

  const defaultValues = useMemo(
    () => ({
      agreedEnd: employment.details?.agreedEnd
        ? new Date(employment.details?.agreedEnd)
        : null,
      endReason: employment.details?.endReason
        ? {
            id: employment.details?.endReason,
            name:
              employmentEndReasons?.[employment.details?.endReason] ||
              employment.details?.endReason,
          }
        : null,
    }),
    [
      employment.details?.agreedEnd,
      employment.details?.endReason,
      employmentEndReasons,
    ],
  );

  const methods = useForm({ defaultValues, mode: "onChange" });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
    setError,
    setValue,
    clearErrors,
  } = methods;

  const { agreedEnd, endReason } = watch();
  const currentAgreedEnd = employment.details?.agreedEnd
    ? new Date(employment.details.agreedEnd)
    : null;
  const showExtendAssignmentsReminder =
    isAdmin &&
    agreedEnd &&
    currentAgreedEnd &&
    isAfter(agreedEnd, currentAgreedEnd);

  useEffect(() => {
    reset(defaultValues);
  }, [employment, reset, defaultValues]);

  useEffect(() => {
    if (agreedEnd) {
      if (!errors.endReason && !endReason) {
        // endReason is required if agreedEnd is set
        setError("endReason", { type: "required" });
      }
    } else {
      clearErrors("endReason");
    }
  }, [agreedEnd, endReason, errors.endReason, setError, clearErrors]);

  const onSubmit = (data: EmploymentBasicInfoFormValues) => {
    // TODO: cancel ending employment?
    if (data.agreedEnd && data.endReason) {
      const formattedDate = format(data.agreedEnd, isoFormat);

      const payload = {
        employmentId: employment.employmentId,
        version: employment.details?.version,
        endDate: formattedDate,
        reason: data.endReason.id,
      };

      dispatch(endEmployment(payload)).then((action) => {
        if (isFulfilled(action)) {
          setEdit(false);
        }
      });
    }
  };

  // FIXME: should this be used?
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onPresend = (data: EmploymentBasicInfoFormValues) => {
    const showWarning =
      data.endReason &&
      data.endReason.id === EmploymentEndReason.ContinuedInAnotherEmployment;
    if (showWarning) {
      pendingSubmitDataRef.current = data;
      setIsConfirmationOpen(true);
      return;
    }
    onSubmit(data);
  };

  const confirmEndEmployment = () => {
    setIsConfirmationOpen(false);
    const data = pendingSubmitDataRef.current;
    if (data) onSubmit(data);
    pendingSubmitDataRef.current = null;
  };

  const onEdit = () => {
    if (!edit) setEdit(true);
    if (edit) return handleSubmit(onPresend)();
  };

  const onCancel = () => {
    setEdit(false);
    reset(defaultValues);
  };

  const validateAgreedEnd = (date: Nullable<Date>) => {
    return !isDateBefore(date, employment.details?.agreedStart);
  };

  const clearEndReason = () => setValue("endReason", null);

  const onAgreedEndChange = (date: Nullable<Date>, fn: Function) => {
    fn(date);
    clearEndReason();
  };

  const saveDisabled = !isEmpty(errors) || !isDirty;

  return (
    <>
      <FormSection
        title={t("basicInfo")}
        edit={edit}
        onEdit={onEdit}
        onCancel={onCancel}
        saveDisabled={saveDisabled}
        readOnly={!canEdit}
      >
        <>
          <Grid
            item
            container
            xs={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("employer")}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography>{employment.details?.employerName}</Typography>
          </Grid>
        </>
        <>
          <Grid
            item
            container
            xs={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("workTitle")}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography>
              {workTitles[employment.details?.workTitleId]}
            </Typography>
          </Grid>
        </>
        <>
          <Grid
            item
            container
            xs={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("collectiveAgreement")}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography>{TESNames[employment.tesId]}</Typography>
          </Grid>
        </>
        <>
          <Grid
            item
            container
            xs={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("talentProfile:employmentType")}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography>
              {employmentTypes[employment.details?.employmentType]}
            </Typography>
          </Grid>
        </>
        <>
          <Grid
            item
            container
            xs={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("talentProfile:reasonForTemporaryEmployment")}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography>
              {tempEmploymentReasons[employment.details?.tempEmploymentReason]}
            </Typography>
          </Grid>
        </>
        <Grid
          item
          container
          xs={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("talentProfile:employmentStarted")}
          </Typography>
        </Grid>
        <Grid item xs={10}>
          <Typography>
            {employment.details?.agreedStart &&
              format(
                new Date(employment.details.agreedStart),
                leadingZerosDateFormat,
              )}
          </Typography>
        </Grid>
        <Grid
          item
          container
          xs={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("talentProfile:agreedEnd")}
          </Typography>
        </Grid>
        <Grid item xs={10}>
          <Controller
            name="agreedEnd"
            control={control}
            rules={{ validate: validateAgreedEnd }}
            render={({ field }) =>
              edit ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <DesktopDatePicker
                    {...field}
                    onChange={(date) => onAgreedEndChange(date, field.onChange)}
                    // Limit to maxDate if agreedEnd is set. Might not be for continuous employment
                    {...(!isAdmin && employment.details?.agreedEnd
                      ? { maxDate: new Date(employment.details.agreedEnd) }
                      : {})}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        size: "small",
                        error: !!errors.agreedEnd,
                        fullWidth: true,
                        sx: { maxWidth: "300px" },
                      },
                    }}
                  />
                  {showExtendAssignmentsReminder && (
                    <Typography color="warning.main" variant="secondary">
                      {t("talentProfile:extendAssignmentsIfNeededReminder")}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography>
                  {field.value
                    ? format(new Date(field.value), leadingZerosDateFormat)
                    : ""}
                </Typography>
              )
            }
          />
        </Grid>
        <>
          <Grid
            item
            container
            xs={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("talentProfile:probationEnded")}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography>
              {employment.details?.probationEndDate
                ? format(
                    new Date(employment.details.probationEndDate),
                    leadingZerosDateFormat,
                  )
                : t("order:noProbationaryPeriod")}
            </Typography>
          </Grid>
        </>
        <Grid
          item
          container
          xs={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("talentProfile:endReason")}
          </Typography>
        </Grid>
        <Grid item xs={10}>
          <Controller
            name="endReason"
            control={control}
            render={({ field }) =>
              edit ? (
                <Autocomplete
                  {...field}
                  options={endReasonOptions}
                  size="small"
                  fullWidth
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={field.value}
                  getOptionLabel={(option) => option.name}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <CommonTextField
                      {...params}
                      sx={{ maxWidth: "300px" }}
                      error={!!errors.endReason}
                    />
                  )}
                />
              ) : (
                <Typography>{field.value?.name}</Typography>
              )
            }
          />
        </Grid>
        <Grid
          item
          container
          xs={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("talentProfile:salaryTypeLabel")}
          </Typography>
        </Grid>
        <Grid item xs={10}>
          <Typography>
            {t(`talentProfile:${employment.details?.wageSystem}`)}
          </Typography>
        </Grid>
      </FormSection>
      <ConfirmationDialog
        isOpen={isConfirmationOpen}
        title={t("talentProfile:endReason")}
        contentText={t("talentProfile:employmentEndWarning")}
        confirmText={t("talentProfile:endEmployment")}
        onConfirm={confirmEndEmployment}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
